import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import {
  Container,
  Typography,
  Grid,
  Paper,
  TextField,
  Button,
  Box,
  CircularProgress,
  Alert
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import nodeApi from '../services/nodeApi';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      console.log('Sending contact form data:', formData);

      // Send the contact form data to the Node.js API
      const response = await nodeApi.contact(formData);

      console.log('Contact form response:', response);

      if (!response || !response.success) {
        const errorMessage = response ? response.message : 'No response from server';
        console.error('Contact form submission failed:', errorMessage);
        setError(errorMessage || 'Failed to send message. Please try again later.');
        return;
      }

      // Show success message
      setSuccess(true);

      // Clear the form
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: ''
      });

    } catch (err) {
      console.error('Error handling form:', err);
      setError(err.message || 'Failed to send message. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 8 }}>
      <Helmet>
        <title>Contact Laprecio | Ethical Mineral Trading Platform</title>
        <meta name="description" content="Contact the Laprecio team for questions about our ethical mineral trading platform, blockchain verification, or to learn more about connecting with African miners." />
        <meta name="keywords" content="contact, Laprecio, mineral trading, ethical minerals, blockchain verification, African miners" />
      </Helmet>
      <Typography variant="h3" component="h1" align="center" gutterBottom>
        Contact Us
      </Typography>

      <Grid container spacing={4} sx={{ mt: 2 }}>
        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 4, height: '100%' }}>
            <Typography variant="h5" gutterBottom>
              Get in Touch
            </Typography>
            <Typography variant="body1" paragraph>
              Have questions about Laprecio? We're here to help. Fill out the form and our team will get back to you as soon as possible.
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', mt: 4 }}>
              <EmailIcon color="primary" sx={{ mr: 2 }} />
              <Typography variant="body1">
                hello@laprecio.com
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'flex-start', mt: 2 }}>
              <LocationOnIcon color="primary" sx={{ mr: 2, mt: 0.5 }} />
              <Typography variant="body1">
                First Floor<br />
                85 Great Portland St<br />
                London W1W 7LT<br />
                United Kingdom
              </Typography>
            </Box>

            <Typography variant="body2" color="textSecondary" sx={{ mt: 4 }}>
              Our team is available Monday to Friday, 9:00 AM to 5:00 PM.
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 4 }}>
            {success && (
              <Alert severity="success" sx={{ mb: 3 }}>
                Your message has been sent successfully to our team at hello@laprecio.com. We'll get back to you soon!
              </Alert>
            )}

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <form id="contact-form" onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    required
                    fullWidth
                    label="Your Name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    required
                    fullWidth
                    label="Your Email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    required
                    fullWidth
                    label="Subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    required
                    fullWidth
                    label="Message"
                    name="message"
                    multiline
                    rows={6}
                    value={formData.message}
                    onChange={handleChange}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    size="large"
                    disabled={loading}
                    sx={{ mt: 2 }}
                  >
                    {loading ? <CircularProgress size={24} /> : 'Send Message'}
                  </Button>
                </Grid>
              </Grid>
            </form>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default ContactPage;
